import type { Maybe } from '@adornis/base/utilTypes.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { useState } from '@adornis/functional-lit/stateful.js';
import { SgSnackbar } from '../../../client/components/ui/sg-snackbar.js';
import { UserListSelectionInterface } from '../entities/UserListSelectionInterface.js';

// TODO move to engine dataview when porting

/**
 * BaseQL drops any request carrying more than 50 Mongo ids, and a selection travels together with
 * other ids in the same request - so the explicit lists are capped below that with some headroom.
 * Bigger selections belong on "Alle auswählen" with a filter, which ships no ids at all.
 */
const MAX_EXPLICIT_SELECTION = 40;

export const useUserSelection = () => {
  /**
   * Explicitly selected person IDs
   */
  const [selectedPersonIDs, setSelectedPersonIDs] = useState<string[]>([]);
  /**
   * Explicitly deselected person IDs. Only relevant if selectedAll is true and the user deselected some persons
   */
  const [explicitDeselectedPersonIDs, setExplicitDeselectedPersonIDs] = useState<string[]>([]);
  /**
   * Whether all persons are selected
   */
  const [selectedAll, setSelectedAll] = useState(false);

  const resetSelection = () => {
    setSelectedPersonIDs([]);
    setExplicitDeselectedPersonIDs([]);
    setSelectedAll(false);
  };

  /**
   *
   * @param filter The filter of the view
   * @returns The selection interface for the current selection. If no selection is made it returns null
   */
  const getSelectionInterface = (filter: Maybe<AdornisFilter>) => {
    const somethingSelected = selectedPersonIDs.length || selectedAll;

    if (!somethingSelected) return null;

    return new UserListSelectionInterface({
      userIDs: selectedPersonIDs,
      filter: selectedAll ? (filter ?? new AdornisFilter({})) : null,
      explicitDeselectedUserIDs: explicitDeselectedPersonIDs,
    });
  };

  const isSelected = (personID: string) => {
    return selectedPersonIDs.includes(personID) || (selectedAll && !explicitDeselectedPersonIDs.includes(personID));
  };

  const toggleSelection = (personID: string) => {
    const isCurrentlySelected = isSelected(personID);

    /**
     * Select all basically reverse the logic for deselected users. As user is selected if it is not in the deselected list.
     */
    if (selectedAll) {
      if (isCurrentlySelected) {
        const deselected = [...new Set([...explicitDeselectedPersonIDs, personID])];
        if (deselected.length > MAX_EXPLICIT_SELECTION) {
          void SgSnackbar.show(
            'Maximal 40 Personen einzeln auswählbar (Temporäre Beschränkung). Bitte "Alle auswählen" mit einem Filter verwenden.',
          );
          return;
        }
        setExplicitDeselectedPersonIDs(deselected);
      } else {
        setExplicitDeselectedPersonIDs(explicitDeselectedPersonIDs.filter(id => id !== personID));
      }
    } else {
      if (isCurrentlySelected) {
        setSelectedPersonIDs(selectedPersonIDs.filter(id => id !== personID));
      } else {
        const selected = [...new Set([...selectedPersonIDs, personID])];
        if (selected.length > MAX_EXPLICIT_SELECTION) {
          void SgSnackbar.show(
            'Maximal 40 Personen einzeln auswählbar (Temporäre Beschränkung). Bitte "Alle auswählen" mit einem Filter verwenden.',
          );
          return;
        }
        setSelectedPersonIDs(selected);
      }
    }
  };

  const toggleSelectAll = () => {
    if (selectedAll) {
      resetSelection();
    } else {
      setSelectedPersonIDs([]);
      setExplicitDeselectedPersonIDs([]);
      setSelectedAll(true);
    }
  };

  return {
    selectedAll,
    hasCustomSelection: () => selectedPersonIDs.length > 0 || explicitDeselectedPersonIDs.length > 0,
    isSelected,
    toggleSelection,
    resetSelection,
    getSelectionInterface,
    toggleSelectAll,
  };
};
